import { SCHEDULE_APPOINTMENT_BASE_PATH } from "@/lib/constants";
import {
  EMERGENCY_BOOKING_VARIANT,
  isExternalSchedulerPath,
  toBookingRouteHref,
  toDispatcherHref,
  type BookingRoute,
} from "@/lib/utils/normalize-slot-hrefs";
import { bookCtaVariantForSlug } from "./book-cta-variant";

/**
 * The ONE href grammar for Book CTAs that BB chrome renders from CODE.
 *
 * normalize-slot-hrefs runs on serialized experience JSON only, so three
 * surfaces never pass through it and police their own hrefs here:
 *   - the Smile Header studio atom (app/components/studio/SmileHeader), which
 *     resolves its Book CTA from the author's bookHref plus the page's route
 *     (resolveSmileBookHref below, D9);
 *   - the SmileButton atom, through lib/utils/smile-chrome/cta-href.ts, for
 *     the same reason;
 *   - the <600px compact chrome (app/components/SmileStickyHeader/
 *     SmileMobileHeader), which re-reads the Book anchor from the rendered
 *     band and from each mega-panel and draws its own pills.
 *
 * The invariant that survives from TAG-163 (an absolute href in the header CTA
 * dropped a wwwstg visitor onto production mid-journey): never render a href
 * that takes the visitor off the environment they are on. An absolute URL on
 * any host, a protocol-relative //host/..., an unrooted path, a traversal, or
 * any href carrying an ASCII control character is refused; a rooted
 * same-origin path is accepted as authored (isSameOriginRelativeHref). The
 * control-character clause is load-bearing, not tidiness: browsers strip tab,
 * LF and CR from a href before parsing it, so "/\t/evil.example.com/" is read
 * as "//evil.example.com/" and lands off-site even though it starts with a
 * single slash (the atom suites pin this on anchor.hostname, the browser's own
 * reading, not the attribute string). An absolute URL on one of OUR
 * hosts is reduced to its own path first, because the path is what the author
 * meant and the origin is the slip.
 *
 * Until 2026-09-08 the allowlist was narrower: only /smile paths passed, and
 * toSmileBookHref forced every Book href onto the internal dispatcher. D1
 * (Chris 2026-09-08, adopting Nathan Schwederske's 2026-09-01 ruling) sent
 * booking to TAG's scheduler on every non-emergency /smile page, so TAG's
 * /schedule-an-appointment/?fc=... is a legitimate, authored Book destination.
 * Eric Zapfe (TAG, Teams 2026-09-10) closed the emergency-page exception:
 * "Emergency scheduler link - currently pointing to the scheduler not in use.
 * Needs updated to BAU schedule link ... Should be:
 * /schedule-an-appointment/emergency/". Every PAGE now routes external and
 * the emergency LPs carry TAG's emergency reason path (EXTERNAL_REASON_PATHS);
 * the "emergency" route survives only for the standalone
 * /smile/schedule/?variant=emergency page's own chrome. The route
 * (book-cta-variant.ts) decides the rewrite through toBookingRouteHref, the
 * same function the server-side JSON pass uses, so bar, drawer and desktop
 * band agree.
 */

/**
 * True only for a relative path already inside /smile. Used for the lockup:
 * the logo always lands on the /smile home. An absolute URL, a protocol-relative
 * `//host/...`, a path outside the sandbox, or a traversal all fail.
 */
export function isSmileRelativeHref(href: string): boolean {
  if (href.includes("..")) return false;
  if (href === "/smile") return true;
  return href.startsWith("/smile/") || href.startsWith("/smile?") || href.startsWith("/smile#");
}

/**
 * True when the href carries any C0 control character (0x00-0x1f) or DEL
 * (0x7f). Browsers delete tab, LF and CR from a URL before parsing it, so a
 * rooted-looking "/\t/host/" becomes protocol-relative "//host/" at
 * navigation time; the rest of the range never belongs in a href either.
 * Checked by code point rather than a character-class regex so the intent is
 * explicit and no lint exception is needed for a control range.
 */
function hasControlCharacter(href: string): boolean {
  for (let i = 0; i < href.length; i += 1) {
    const code = href.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}

/**
 * True for a rooted path on the current origin: carries no control
 * characters (see hasControlCharacter), starts with a single `/`, is not
 * protocol-relative (`//host` or the `/\host` form browsers read the same
 * way), and does not climb with `..`. Anything with a scheme (https:, tel:,
 * javascript:) or without a leading slash fails.
 */
export function isSameOriginRelativeHref(href: string): boolean {
  if (hasControlCharacter(href)) return false;
  if (!href.startsWith("/")) return false;
  if (href.startsWith("//") || href.startsWith("/\\")) return false;
  return !href.includes("..");
}

/**
 * The site's own hosts. Any environment counts, not just production: an
 * authored `https://wwwstg.aspendental.com/...` on a production page is the
 * same defect in the other direction.
 */
function isAspenDentalHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return host === "aspendental.com" || host.endsWith(".aspendental.com");
}

/**
 * Reduces an absolute aspendental.com URL to its path + query + hash. Returns
 * null for anything that is not an absolute URL on one of our hosts, so the
 * caller can fall through to the relative-path rule (and refuse off-site
 * origins rather than quietly adopting their path).
 */
export function toSameSitePath(candidate: string): string | null {
  if (!/^https?:\/\//i.test(candidate)) return null;
  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return null;
  }
  if (!isAspenDentalHost(url.hostname)) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * TAG's scheduler reason path per scheduler-registry variant slug
 * (app/smile/schedule/variants.ts), the grammar the D1 mega-menu pills and the
 * stamped page bands book with on 2026-09-08: dentures -> /dentures/, implants
 * and tooth replacement -> /implants/, clear aligners -> /mottoaligners/, and
 * checkup -> /other/ (D15: TAG's booking backend has no checkup token, TAG's
 * own scheduler labels the Other radio "Other/Check Up"), and emergency ->
 * /emergency/ (Eric Zapfe, TAG, 2026-09-10: the emergency LPs book TAG's BAU
 * scheduler, /schedule-an-appointment/emergency/, not the internal
 * dispatcher). The other emergency-fastpath rows (tooth-pain, ...) are
 * /smile/schedule/?variant= rows no page slug maps to and stay absent. A slug
 * the registry does not name books on the bare scheduler, exactly as the
 * homepage, insurance and dental-services bands do.
 */
export const EXTERNAL_REASON_PATHS: Readonly<Record<string, string>> = {
  dentures: "dentures/",
  "denture-repair": "dentures/",
  implants: "implants/",
  "tooth-replacement": "implants/",
  "clear-aligners": "mottoaligners/",
  checkup: "other/",
  emergency: "emergency/",
};

/**
 * TAG's "on page scheduler" marker. TAG's own header CTA (useScheduleUrl in
 * app/components/Header/hooks) and the authored /smile band both send the
 * scheduler `?src=ops`, and the legacy push wrapper the scheduler runs
 * (lib/utils/dataLayerHelpers.ts, resolveSchedulerType) reports GA4
 * scheduler_type "on page" for a src=ops visit and "bau" for anything else.
 * A header pill that drops the marker is not a cosmetic delta: every
 * appointment_step of that journey changes bucket. So every header href onto
 * TAG's scheduler carries it: the route default (D9-Q2, 2026-09-08) and, since
 * 2026-09-10, an authored one that names no `src` of its own
 * (withSchedulerParams). The emergency dispatcher never gets it: toDispatcherHref drops src on the way to /smile/schedule/, and the
 * band's served emergency href (`?variant=emergency&facilityCode=1103`)
 * carries none, so the atom matches the band there too.
 */
export const ON_PAGE_SCHEDULER_SOURCE = "src=ops";

/**
 * Whether a code-resolved Book href carries an office. `chip`, the default
 * since Chris Mason's 2026-09-10 ruling (production /smile/ with the chip on
 * Homestead: the header's Book Online "is not passing the parameter like the
 * buttons on page"), appends the office the header CHIP shows - the nearest
 * office the geo ladder resolved, or the site's selected office the chip
 * painted first - as `fc=` on TAG's scheduler or `facilityCode=` on the
 * emergency dispatcher, so the chip and Book Online can never name two
 * different offices. The page's own Book CTAs (TAG's Button / LinkWrapper,
 * lib/utils/facilityCodeLink.ts) append `fc=` for the site's selected office
 * the same way; the header follows its chip (D9-Q1). `none` is the opt-out:
 * the plain scheduler path, no pin. A literal office code (the band's
 * `?fc=1103`) is content, authored in the atom's Book CTA Href: no code path
 * here ever invents one, and an authored pin is never overwritten.
 */
export type SmileBookOfficePin = "none" | "chip";

interface ResolveSmileBookHrefInput {
  /** The author's Book CTA Href; empty or whitespace means "let the page decide". */
  authored?: string | null;
  /** The page's booking route (bookingRouteForSlug / bookingRouteForScheduleRoute). */
  route: BookingRoute;
  /** The plain page slug, for the reason path; null on the static /smile/schedule/ mount. */
  slugPath?: string | null;
  /** The facility code of the office the header chip shows, used only when officePin is "chip". */
  officeCode?: string | null;
  /** Defaults to "chip" (SmileBookOfficePin). */
  officePin?: SmileBookOfficePin;
}

interface ResolvedSmileBookHref {
  href: string;
  /** "authored" when the author's value stands (routed and same-origin), "route" when the page decided. */
  source: "authored" | "route";
}

/**
 * Where a Book pill lands when nothing is authored, per route, BEFORE the
 * header grammar is applied: the emergency dispatcher on the emergency route;
 * TAG's scheduler on the external route, carrying the page's reason path when
 * the registry names the page. withSchedulerParams then adds TAG's src=ops
 * marker and, when known, the chip's office.
 */
function routeDefaultBookHref(route: BookingRoute, slugPath: string | null | undefined): string {
  if (route === "emergency") return toDispatcherHref("", EMERGENCY_BOOKING_VARIANT);
  const variant = slugPath ? bookCtaVariantForSlug(slugPath) : null;
  const reason = variant ? (EXTERNAL_REASON_PATHS[variant] ?? "") : "";
  return `${SCHEDULE_APPOINTMENT_BASE_PATH}${reason}`;
}

/**
 * Where a Book pill lands when the band ships no scheduler anchor at all, per
 * route and (optionally) page slug: the same answer resolveSmileBookHref gives
 * an unauthored atom with no office, so the <600px bar pill and the desktop
 * atom agree to the character on a band-less page. Neither invents an office
 * pin.
 */
export function defaultBookHref(route: BookingRoute, slugPath: string | null = null): string {
  return withSchedulerParams(routeDefaultBookHref(route, slugPath), null, "none");
}

/** The internal dispatcher family (/smile/schedule/...), whose office pin is `facilityCode=`. */
function isDispatcherPath(path: string): boolean {
  return path === "/smile/schedule" || path.startsWith("/smile/schedule/");
}

/** True when the query names `key` (encoded keys and repeats read like the browser does). */
function hasParam(query: string, key: string): boolean {
  return new URLSearchParams(query).has(key);
}

/**
 * The header's scheduler grammar, the same parameters the page's Book CTAs
 * carry. Chris Mason, 2026-09-10, production /smile/ with the chip resolved to
 * Homestead: the header's Book Online "is not passing the parameter like the
 * buttons on page" - the page CTAs read `?fc=1103` (TAG's Button / LinkWrapper
 * append the selected office, lib/utils/facilityCodeLink.ts) while the header
 * atom's authored bare `/schedule-an-appointment/` stood untouched, and on
 * wwwstg the route default read `?src=ops` with no office. Applied to whatever
 * href the resolver settled on, authored or route default:
 *
 *   - a href onto TAG's scheduler (/schedule-an-appointment/...) gains
 *     `fc=<office>` when the pin is "chip" and an office is known, then
 *     `src=ops` (ON_PAGE_SCHEDULER_SOURCE): `?fc=1103&src=ops`, the order
 *     TAG's own header (useScheduleUrl) and the authored band send;
 *   - a href onto the internal dispatcher (/smile/schedule/...) gains
 *     `facilityCode=<office>` under the same pin rule and never `src`
 *     (toDispatcherHref drops it; the dispatcher has no such bucket);
 *   - any other href (a /smile page, /dentist/, tel:) comes back untouched.
 *
 * Never duplicates: a query that already names `fc`/`facilityCode` keeps the
 * author's office, one that already names `src` keeps the author's marker.
 * Existing params are kept byte for byte (appended to, never re-encoded), a
 * hash rides along, and the result is idempotent.
 */
function withSchedulerParams(
  href: string,
  officeCode: string | null | undefined,
  officePin: SmileBookOfficePin,
): string {
  const hashIndex = href.indexOf("#");
  const hash = hashIndex === -1 ? "" : href.slice(hashIndex);
  const beforeHash = hashIndex === -1 ? href : href.slice(0, hashIndex);
  const queryIndex = beforeHash.indexOf("?");
  const path = queryIndex === -1 ? beforeHash : beforeHash.slice(0, queryIndex);
  const external = isExternalSchedulerPath(path);
  if (!external && !isDispatcherPath(path)) return href;
  const query = queryIndex === -1 ? "" : beforeHash.slice(queryIndex + 1);
  const additions: string[] = [];
  const code = officePin === "chip" ? officeCode?.trim() : undefined;
  if (code && !hasParam(query, "fc") && !hasParam(query, "facilityCode")) {
    additions.push(`${external ? "fc" : "facilityCode"}=${encodeURIComponent(code)}`);
  }
  if (external && !hasParam(query, "src")) additions.push(ON_PAGE_SCHEDULER_SOURCE);
  if (additions.length === 0) return href;
  const separator = query === "" || query.endsWith("&") ? "" : "&";
  return `${path}?${query}${separator}${additions.join("&")}${hash}`;
}

/**
 * The Smile Header atom's Book CTA (D9), page-aware and office-aware:
 *
 *   1. an authored href stands when it is a rooted same-origin path after the
 *      route is applied (emergency rewrite through toBookingRouteHref, the
 *      TAG-163 guard) -> source "authored". An absolute URL on ANY host, our
 *      own included, is refused here, unlike toSmileBookHref below: that
 *      function re-reads anchors the server JSON pass already normalized,
 *      while this one reads what an author typed into a code-rendered atom,
 *      and the atom's contract since 2026-09-04 is that a typed environment
 *      never ships (an absolute staging URL on a production page is the same
 *      defect the other way round);
 *   2. empty, whitespace, or refused -> the route's default (emergency
 *      dispatcher, or TAG's scheduler with the page's reason path) -> source
 *      "route";
 *   3. then the header grammar, whichever branch won (withSchedulerParams,
 *      Chris Mason 2026-09-10): a scheduler href gains the chip's office
 *      (officePin "chip", the default; "none" opts out) and TAG's src=ops
 *      marker where it lacks them, so an authored bare
 *      `/schedule-an-appointment/` books `?fc=1103&src=ops` exactly like the
 *      page's Book CTAs, and an authored pin or marker is the author's.
 *
 * Pure and idempotent: resolving a resolved href again changes nothing.
 */
export function resolveSmileBookHref({
  authored,
  route,
  slugPath = null,
  officeCode = null,
  officePin = "chip",
}: ResolveSmileBookHrefInput): ResolvedSmileBookHref {
  const candidate = authored?.trim();
  let resolved: ResolvedSmileBookHref | null = null;
  if (candidate) {
    const routed = toBookingRouteHref(candidate, route);
    if (isSameOriginRelativeHref(routed)) resolved = { href: routed, source: "authored" };
  }
  if (!resolved) resolved = { href: routeDefaultBookHref(route, slugPath), source: "route" };
  return { ...resolved, href: withSchedulerParams(resolved.href, officeCode, officePin) };
}

/**
 * Resolve a sourced Book href for the page's booking route to one that is
 * guaranteed same-origin relative:
 *
 *   - an http(s) href on one of our hosts -> its path + query + hash (the
 *     origin goes, so the visitor stays on the environment they are on);
 *   - then toBookingRouteHref: on the emergency route a scheduler href of
 *     either grammar becomes `/smile/schedule/?variant=emergency[&facilityCode=]`;
 *     on the external route the authored href stands;
 *   - a result that is still not a rooted same-origin path (empty, off-site
 *     absolute, protocol-relative, traversal, unrooted) -> defaultBookHref
 *     for the route AND the page (TAG-191): the <600px chrome hands over the
 *     slug SmileChromeRouteProvider carries, so a refused or missing anchor
 *     lands on the same href the desktop atom resolves for that page
 *     (`/schedule-an-appointment/dentures/?src=ops` on the dentures page),
 *     never a slug-less generic one the atom beside it would disagree with.
 *     No slug (the static /smile/schedule mount, a caller with no page) keeps
 *     the bare scheduler.
 */
export function toSmileBookHref(
  href: string | null | undefined,
  route: BookingRoute,
  slugPath: string | null = null,
): string {
  const candidate = href?.trim();
  if (!candidate) return defaultBookHref(route, slugPath);
  const relative = toSameSitePath(candidate) ?? candidate;
  const routed = toBookingRouteHref(relative, route);
  return isSameOriginRelativeHref(routed) ? routed : defaultBookHref(route, slugPath);
}
